<template>
  <InfoList
    v-if="isViewPermission"
    :title="$t('pages.user_profile.profile.comments.title')"
    :button-label="$t('action.add_comment')"
    :is-loading="store.comments.length === 0 && store.isLoading"
    :on-click="isAddPermission && openModal.add">
    <InfoListComment
      v-for="comment in store.comments"
      :key="`comment_${comment.id}`"
      v-bind="comment"
      @edit="openModal.edit(comment)"
      @remove="openModal.remove(comment)"/>
    <div
      v-show="store.comments.length === 0"
      class="text-old-n-black-80">
      {{ $t('pages.user_profile.profile.comments.no_comments') }}
    </div>
    <FormKit
      v-show="store.comments.length !== 0 && !store.isAllLoaded"
      :loading="store.isLoading"
      type="button"
      :label="$t('action.show_more')"
      :suffix-icon="store.isLoading && 'spinner'"
      suffix-icon-class="animate-spin ml-4"
      outer-class="mb-0"
      input-class="text small fit"
      @click="store.getComments(applicationId)"/>
  </InfoList>
  <OnboardingCommentAddForm
    v-if="isModalOpen"
    v-model:is-modal-open="isModalOpen"
    :application-id="applicationId"
    v-bind="formProps"/>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import InfoList from '@/components/InfoList.vue';
import InfoListComment from '@/components/InfoListComment.vue';
import OnboardingCommentAddForm, { FormProps } from '@/modules/onboarding-dashboard/shared/components/OnboardingCommentAddForm.vue';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { useOnboardingCommentsStore } from '@/stores/OnboardingComments';
import { useUserDataStore } from '@/stores/UserData';

type PartialFormProps = Omit<FormProps, 'applicationId'>;

const { applicationId } = defineProps<{ applicationId: string }>();

const { havePermissions } = useUserDataStore();
const store = useOnboardingCommentsStore();

const isModalOpen = ref<boolean>(false);
const isViewPermission = computed<boolean>(() => havePermissions(['ONBOARDING_COMMENTS_MANAGER', 'ONBOARDING_COMMENTS_VIEWER']));
const isAddPermission = computed<boolean>(() => havePermissions('ONBOARDING_COMMENTS_MANAGER'));
const formProps = ref<PartialFormProps>({ type: 'add' });

function baseOpenModal(props: PartialFormProps) {
  formProps.value = props;
  isModalOpen.value = true;
}

const openModal = {
  add: () => baseOpenModal({ type: 'add' }),
  edit: (comment: Onboarding.Comment) => baseOpenModal({ type: 'edit', comment }),
  remove: (comment: Onboarding.Comment) => baseOpenModal({ type: 'remove', comment }),
};

onMounted(() => store.getComments(applicationId));
</script>
